import clsx from 'clsx'
import { Button, Container, Icon, NetworkIcon } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link } from 'react-router-dom'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Services/Campaigns/Main/subcomponents/List/Campaign/campaign.module.scss'
import { ROUTES } from 'constants/routes'

const Campaign = ({
  accountId,
  id,
  name,
  product,
  status,
  company,
  infoOnly = false,
  onDeleteCampaign = () => {},
}) => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const breakpoints = useBreakpoint()

  const showStartButton = useMemo(
    () => status === 'PAUSED' || status === 'WAITING' || status === 'FINISHED',
    [status],
  )

  return (
    <Container display={'flex'} flexJustify={'space-between'}>
      <Container display={'flex'}>
        {!infoOnly && !breakpoints.md && (
          <Container display={'flex'}>
            <NetworkIcon
              name={'yandex-business'}
              size={'m'}
              className={styles['campaign__partner-icon']}
            />
          </Container>
        )}
        <Container display={'flex'} flexDirection={'column'}>
          <div className={styles['campaign__name']}>{name}</div>
          <div className={styles['campaign__company-name']}>{company.name}</div>
          <div className={styles['campaign__product-label-bar']}>
            <span className={styles['campaign__product-type-label']}>
              {t(
                `services::campaigns_list.campaign.type.${product.toLowerCase()}`,
              )}
            </span>
          </div>
          <div className={styles['campaign__label-bar']}>
            <span className={styles['campaign__id-label']}>№ {id}</span>
            <span
              className={clsx(
                styles['campaign__status-label'],
                styles[`campaign__status_${status.toLowerCase()}`],
              )}
            >
              {t(
                `services::campaigns_list.campaign.status.${status.toLowerCase()}`,
              )}
            </span>
          </div>

          {!infoOnly && (
            <Container className={styles['campaign__action-buttons-container']}>
              {showStartButton && (
                <Button
                  size={'s'}
                  startIcon={
                    <Icon
                      name={'arrow2-small'}
                      color={'basic-primary-white'}
                      size={'s'}
                    />
                  }
                  variant={'contained'}
                  component={Link}
                  to={pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT_CAMPAIGN_START, {
                    networkType: 'yandex-business',
                    accountId,
                    campaignId: id,
                  })}
                >
                  {t(`services::campaigns_list.campaign.action_buttons.start`)}
                </Button>
              )}

              {!company.is_owner && showStartButton && (
                <Button
                  size={'s'}
                  startIcon={
                    <Icon
                      name={'checkmark1'}
                      color={'basic-primary-white'}
                      size={'s'}
                    />
                  }
                  component={'a'}
                  variant={'contained'}
                  target={'_blank'}
                  href={`https://yandex.ru/business/priority/campaign/${id}/main`}
                >
                  {t(`services::campaigns_list.campaign.action_buttons.verify`)}
                </Button>
              )}
            </Container>
          )}
        </Container>
      </Container>
      <Container
        display={'flex'}
        flexAlign={'flex-start'}
        flexDirection={'column'}
      >
        <div>
          {!breakpoints.sm ? (
            <Button
              size={breakpoints.sm ? 's' : 'm'}
              color={'text-secondary'}
              className={styles['campaign__external-link']}
              startIcon={
                <Icon
                  name={'settings'}
                  color={'text-secondary'}
                  size={breakpoints.sm ? 's' : 'm'}
                />
              }
              variant={'link'}
              component={'a'}
              href={`https://yandex.ru/business/priority/campaign/${id}/main`}
              target={'_blank'}
            >
              {t('services::campaigns_list.campaign.action_buttons.settings')}
            </Button>
          ) : (
            <Button
              noLabel
              target={'_blank'}
              startIcon={<Icon name={'settings'} />}
              color={'text-secondary'}
              component={'a'}
              href={`https://yandex.ru/business/priority/campaign/${id}/main`}
              size={breakpoints.sm ? 's' : 'm'}
            />
          )}
        </div>
        {['FINISHED', 'STOPPED', 'WAITING'].includes(status) && (
          <div>
            {!breakpoints.sm ? (
              <Button
                onClick={() => onDeleteCampaign(id)}
                size={breakpoints.sm ? 's' : 'm'}
                color={'text-secondary'}
                className={styles['campaign__external-link']}
                startIcon={
                  <Icon
                    name={'trash'}
                    color={'text-secondary'}
                    size={breakpoints.sm ? 's' : 'm'}
                  />
                }
                variant={'link'}
              >
                {t('services::campaigns_list.campaign.action_buttons.delete')}
              </Button>
            ) : (
              <Button
                noLabel
                startIcon={<Icon name={'trash'} />}
                onClick={() => onDeleteCampaign(id)}
                target={'_blank'}
                color={'text-secondary'}
                size={breakpoints.sm ? 's' : 'm'}
              />
            )}
          </div>
        )}
      </Container>
    </Container>
  )
}

Campaign.propTypes = {
  infoOnly: PropTypes.bool,
  i18n: PropTypes.string,
  accountId: PropTypes.string,
  name: PropTypes.string,
  onDeleteCampaign: PropTypes.func,
  id: PropTypes.number,
  product: PropTypes.oneOf(['SUBSCRIPTION', 'GEO', 'WEB']),
  status: PropTypes.oneOf([
    'DELAYED_START',
    'FINISHED',
    'PAUSED',
    'RUNNING',
    'STOPPED',
    'WAITING',
  ]),
  company: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    is_owner: PropTypes.bool,
  }),
  created_at: PropTypes.string,
  budget: PropTypes.shape({ duration: PropTypes.number }),
}

export default Campaign
